import type { Breakpoint } from '@ctw/config/zui';
import type { DockableDrawerProps } from '@ctw/shared/components/containers/dockable-drawer';
import { LoadingSpinner } from '@ctw/shared/components/loading-spinner';
import { Sheet } from '@ctw/shared/components/sheet';
import { DrawerProvider, type useDrawer, useDrawerState } from '@ctw/shared/context/drawer-context';
import { type ModalLayout, ModalProvider } from '@ctw/shared/context/modal-context';
import { ResourceDrawerProvider } from '@ctw/shared/context/resource-drawer-context';
import { ResourceModalProvider } from '@ctw/shared/context/resource-modal-context';
import {
  TelemetryBoundary,
  type useTelemetry,
} from '@ctw/shared/context/telemetry/telemetry-boundary';
import { useZuiContext } from '@ctw/shared/context/zui-provider';
import { useBreakpointQuery } from '@ctw/shared/hooks/breakpoints';
import {
  DETAIL_CONTENT_SHEET_ID,
  DETAIL_CONTENT_SHEET_TEST_ID,
  MAIN_CONTENT_ID,
  MAIN_CONTENT_SHEET_ID,
  MAIN_CONTENT_SHEET_TEST_ID,
} from '@ctw/shared/test/ids';
import { type ClassName, tw, twx } from '@ctw/shared/utils/tailwind';
import { Transition } from '@headlessui/react';
import {
  Fragment,
  type PropsWithChildren,
  type ReactNode,
  createContext,
  forwardRef,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

interface MainContentWithDockableDrawerState {
  setHeaderContent: (content: ReactNode | null) => void;
  headerContent: ReactNode | null;
  setDetailPanelContent: (content: ReactNode | null) => void;
  detailPanelContent: ReactNode | null;
}

const MainContentWithDockableDrawerContext =
  createContext<MainContentWithDockableDrawerState | null>(null);

interface MainContentWithDockableDrawerProviderProps {
  children: (state: MainContentWithDockableDrawerState) => ReactNode;
}

const MainContentWithDockableDrawerProvider = ({
  children,
}: MainContentWithDockableDrawerProviderProps) => {
  const [headerContent, setHeaderContent] = useState<ReactNode | null>(null);
  const [detailPanelContent, setDetailPanelContent] = useState<ReactNode | null>(null);

  const state: MainContentWithDockableDrawerState = useMemo(
    () => ({
      headerContent,
      setHeaderContent,
      detailPanelContent,
      setDetailPanelContent,
    }),
    [headerContent, detailPanelContent],
  );

  return (
    <MainContentWithDockableDrawerContext.Provider value={state}>
      {children(state)}
    </MainContentWithDockableDrawerContext.Provider>
  );
};

export interface MainContentWithDockableDrawerProps extends PropsWithChildren {
  className?: ClassName;
  contentContainerClassName?: ClassName;
  dockableDrawerContainerClassName?: ClassName;
  focalHeader?: boolean;
  fullbleed?: boolean;
  handleContentLoadingState?: boolean;
  enableDeepLinking?: boolean;
  dockingMode?: DockableDrawerProps['dockingMode'];
  dockDisplay?: DockableDrawerProps['dockDisplay'];
  modalLayout?: ModalLayout;
  mainContentHiddenBelow?: Breakpoint;
  onResourceDrawerFirstViewBackButtonClick?: (args: {
    drawer: ReturnType<typeof useDrawer>;
  }) => void;
}

export const MainContentWithDockableDrawer = ({
  handleContentLoadingState = false,
  contentContainerClassName,
  enableDeepLinking = true,
  dockingMode,
  dockDisplay,
  onResourceDrawerFirstViewBackButtonClick,
  modalLayout,
  mainContentHiddenBelow = 'lg',
  ...mainContentAndDockedDrawerProps
}: MainContentWithDockableDrawerProps) => {
  const { breakpoints } = useBreakpointQuery();

  return (
    <MainContentWithDockableDrawerProvider>
      {({ detailPanelContent }) => (
        <div
          className={twx('flex w-full max-w-full gap-3 overflow-hidden', contentContainerClassName)}
        >
          <MainContentSheet
            fullbleed={true}
            handleContentLoadingState={handleContentLoadingState}
            className={twx('transition-all duration-300', {
              hidden: Boolean(detailPanelContent) && !breakpoints.isAtLeast[mainContentHiddenBelow],
              'w-full': !detailPanelContent,
              'w-[293px] min-w-[293px] max-w-[293px]':
                Boolean(detailPanelContent) && breakpoints.isAtLeast[mainContentHiddenBelow],
            })}
          >
            <ModalProvider defaultLayout={modalLayout}>
              {({ modal }) => (
                <ResourceModalProvider
                  enableDeepLinking={enableDeepLinking}
                  defaultLayout={modalLayout}
                >
                  <DrawerProvider
                    allowDocking={true}
                    dockingMode={dockingMode}
                    dockDisplay={dockDisplay}
                  >
                    {({ drawer }) => (
                      <ResourceDrawerProvider
                        enableDeepLinking={enableDeepLinking}
                        onFirstViewBackButtonClick={onResourceDrawerFirstViewBackButtonClick}
                      >
                        <MainContentAndDockedDrawer
                          {...mainContentAndDockedDrawerProps}
                          dockDisplay={dockDisplay}
                          drawerIsHidden={Boolean(detailPanelContent)}
                          drawer={drawer}
                        />
                      </ResourceDrawerProvider>
                    )}
                  </DrawerProvider>
                  {modal}
                </ResourceModalProvider>
              )}
            </ModalProvider>
          </MainContentSheet>
          <Transition show={Boolean(detailPanelContent)} as={Fragment}>
            <DetailContentSheet
              fullbleed={true}
              className={tw`data-[enter]:data-[closed]:-translate-x-full data-[leave]:data-[closed]:translate-x-full data-[closed]:w-0 data-[open]:w-full data-[open]:flex-1 data-[closed]:opacity-0 data-[open]:opacity-100 data-[enter]:duration-300 data-[leave]:duration-300`}
            >
              {detailPanelContent}
            </DetailContentSheet>
          </Transition>
        </div>
      )}
    </MainContentWithDockableDrawerProvider>
  );
};

interface MainContentAndDockedDrawerProps extends MainContentWithDockableDrawerProps {
  drawer: ReactNode;
  drawerIsHidden: boolean;
  dockableDrawerContainerClassName?: ClassName;
  dockDisplay: DockableDrawerProps['dockDisplay'];
}

const MainContentAndDockedDrawer = ({
  className,
  children,
  focalHeader,
  fullbleed,
  drawer,
  drawerIsHidden,
  dockDisplay,
  dockableDrawerContainerClassName,
}: MainContentAndDockedDrawerProps) => {
  const { isDrawerDocked } = useDrawerState();

  return (
    <div className={tw`flex h-full min-h-full w-full min-w-full`}>
      <main
        id={MAIN_CONTENT_ID}
        className={twx(
          'relative flex max-h-full w-full min-w-0 max-w-full grow-0 flex-col transition-all duration-300',
          {
            'max-[1100px]:!w-0 max-[1100px]:!max-w-0 max-[1100px]:!overflow-hidden':
              isDrawerDocked && !drawerIsHidden && dockDisplay === 'detail',
          },
          className,
        )}
      >
        <MainContentHeader focalHeader={focalHeader} />
        <div
          className={twx('scroll-shadows h-full max-w-full overflow-auto', {
            'p-2': !fullbleed,
          })}
        >
          {children}
        </div>
      </main>
      <div
        className={twx(
          'border-divider-main border-l transition-all duration-300',
          {
            hidden: drawerIsHidden,
            'max-[1100px]:w-full': !drawerIsHidden && isDrawerDocked && dockDisplay === 'detail',
            'w-min': !isDrawerDocked && dockDisplay === 'detail',
          },
          dockableDrawerContainerClassName,
        )}
      >
        {drawer}
      </div>
    </div>
  );
};

export interface MainContentSheetProps extends PropsWithChildren {
  className?: string;
  fullbleed?: boolean;
  handleContentLoadingState: boolean;
}

export const MainContentSheet = forwardRef<HTMLElement, MainContentSheetProps>(
  ({ className, children, handleContentLoadingState, fullbleed }, ref) => {
    const { isNavigating } = useZuiContext();

    return (
      <Sheet
        ref={ref}
        id={MAIN_CONTENT_SHEET_ID}
        testId={MAIN_CONTENT_SHEET_TEST_ID}
        fullbleed={fullbleed}
        className={twx('flex flex-1 flex-col transition-all duration-300', className)}
      >
        {isNavigating && handleContentLoadingState ? (
          <LoadingSpinner centered={true} message="Loading..." />
        ) : (
          children
        )}
      </Sheet>
    );
  },
);

export interface DetailContentSheetProps extends PropsWithChildren {
  className?: string;
  fullbleed?: boolean;
}

export const DetailContentSheet = forwardRef<HTMLElement, DetailContentSheetProps>(
  ({ className, children, fullbleed }, ref) => {
    return (
      <Sheet
        ref={ref}
        id={DETAIL_CONTENT_SHEET_ID}
        testId={DETAIL_CONTENT_SHEET_TEST_ID}
        fullbleed={fullbleed}
        className={twx(
          'max-h-full min-h-full w-fit max-w-full flex-1 overflow-hidden transition-all duration-300',
          className,
        )}
      >
        {children}
      </Sheet>
    );
  },
);

interface MainContentHeaderProps extends Pick<MainContentWithDockableDrawerProps, 'focalHeader'> {}

const MainContentHeader = ({ focalHeader }: MainContentHeaderProps) => {
  const context = useContext(MainContentWithDockableDrawerContext);

  return (
    <>
      {context?.headerContent && (
        <div
          className={twx('flex h-fit w-full items-center border-divider-main p-2', {
            'bg-content-focal': focalHeader,
            'border-b bg-white': !focalHeader,
          })}
        >
          {context.headerContent}
        </div>
      )}
    </>
  );
};

interface UseMainContentHeaderContentArgs {
  telemetry: ReturnType<typeof useTelemetry>;
}

export const useMainContentHeader = (
  boundaryName: string,
  content: (args: UseMainContentHeaderContentArgs) => ReactNode,
  dependencies: Array<unknown>,
): void => {
  const context = useContext(MainContentWithDockableDrawerContext);

  // biome-ignore lint/correctness/useExhaustiveDependencies: No explanation was provided. Please be a good citizen and add one!
  useLayoutEffect(() => {
    context?.setHeaderContent(
      <TelemetryBoundary boundaryName={boundaryName}>
        {({ telemetry }) => content({ telemetry })}
      </TelemetryBoundary>,
    );

    return () => {
      context?.setHeaderContent(null);
    };
  }, dependencies);
};

interface UseMainContentDetailPanelContentArgs {
  telemetry: ReturnType<typeof useTelemetry>;
}

export const useMainContentDetailPanel = (
  boundaryName: string,
  content: ((args: UseMainContentDetailPanelContentArgs) => ReactNode) | null,
  dependencies: Array<unknown>,
): void => {
  const context = useContext(MainContentWithDockableDrawerContext);

  const { registerZuiEventListener } = useZuiContext();

  // biome-ignore lint/correctness/useExhaustiveDependencies: No explanation was provided. Please be a good citizen and add one!
  useLayoutEffect(
    () => registerZuiEventListener('beforeNavigate', () => context?.setDetailPanelContent(null)),
    [context],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: No explanation was provided. Please be a good citizen and add one!
  useLayoutEffect(() => {
    if (content) {
      context?.setDetailPanelContent(<LoadingSpinner centered={true} message="Loading..." />);

      // Render first with a loading indicator to kick off the animation, then swap to the real
      // content after the animation is underway. This prevents render-blocking content from
      // jittering the animation
      setTimeout(
        () =>
          context?.setDetailPanelContent(
            <TelemetryBoundary boundaryName={boundaryName}>
              {({ telemetry }) => content({ telemetry })}
            </TelemetryBoundary>,
          ),
        200,
      );
    } else {
      context?.setDetailPanelContent(null);
    }

    return () => {
      context?.setDetailPanelContent(null);
    };
  }, dependencies);
};
